import React, { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle, XCircle, ArrowLeft, Printer, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => new Date().toISOString().slice(0, 8) + "01";
const currentPeriod = () => new Date().toISOString().slice(0, 7);

export default function Reports() {
  const [tab, setTab] = useState("gl");
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [period, setPeriod] = useState(currentPeriod());
  const [accounts, setAccounts] = useState([]);
  const [glSummary, setGlSummary] = useState([]);
  const [glDetail, setGlDetail] = useState(null);
  const [selectedAcc, setSelectedAcc] = useState("");
  const [tb, setTb] = useState(null);
  const [spt, setSpt] = useState(null);

  const loadCoa = () => api.get("/accounting/accounts").then((r) => setAccounts(r.data));
  const loadGlSummary = () => api.get(`/reports/general-ledger?date_from=${dateFrom}&date_to=${dateTo}`).then((r) => setGlSummary(r.data.accounts || []));
  const loadGlDetail = (code) => {
    setSelectedAcc(code);
    api.get(`/reports/general-ledger?account_code=${code}&date_from=${dateFrom}&date_to=${dateTo}`).then((r) => setGlDetail(r.data));
  };
  const loadTb = () => api.get(`/reports/trial-balance?date_from=${dateFrom}&date_to=${dateTo}`).then((r) => setTb(r.data));
  const loadSpt = () => api.get(`/reports/spt-ppn?period=${period}`).then((r) => setSpt(r.data));

  useEffect(() => { loadCoa(); }, []);
  useEffect(() => {
    if (tab === "gl" && !selectedAcc) loadGlSummary();
    if (tab === "gl" && selectedAcc) loadGlDetail(selectedAcc);
    if (tab === "tb") loadTb();
    if (tab === "spt") loadSpt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dateFrom, dateTo, period]);

  const printFinancialReport = async () => {
    try {
      const token = localStorage.getItem("nusa_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/accounting/reports/financial-statement/pdf?date_from=${dateFrom}&date_to=${dateTo}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.detail || "Gagal cetak"); }
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.target = "_blank";
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 60000);
      toast.success("Laporan Keuangan dibuka — Neraca + Laba Rugi");
    } catch (e) { toast.error(e.message || "Gagal"); }
  };

  const printPage = () => window.print();

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="reports-page">
      <div className="flex justify-between items-baseline flex-wrap gap-2">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Laporan Keuangan</h1>
          <p className="text-sm text-slate-500 mt-1">Buku Besar per akun & Neraca Saldo untuk closing bulanan.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={printFinancialReport} data-testid="print-financial-report-btn" className="border-slate-900 text-slate-900 hover:bg-slate-50"><Printer size={14} className="mr-1" />Cetak Laporan Keuangan</Button>
          <Button variant="outline" size="sm" onClick={printPage} data-testid="print-btn"><Printer size={14} className="mr-1" />Cetak Halaman</Button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-4 flex items-end gap-3 flex-wrap">
        <div><Label>Dari Tanggal</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" /></div>
        <div><Label>Sampai Tanggal</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" /></div>
        <div className="ml-auto text-xs text-slate-500">Periode: {formatDate(dateFrom)} — {formatDate(dateTo)}</div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelectedAcc(""); setGlDetail(null); }} className="mt-4">
        <TabsList>
          <TabsTrigger value="gl" data-testid="tab-gl">Buku Besar</TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-tb">Neraca Saldo</TabsTrigger>
          <TabsTrigger value="spt" data-testid="tab-spt">SPT Masa PPN</TabsTrigger>
        </TabsList>

        <TabsContent value="gl" className="mt-4">
          {!selectedAcc ? (
            <div className="bg-white border border-slate-200 rounded-md" data-testid="gl-summary">
              <Table>
                <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Akun</TableHead><TableHead className="text-right">Total Debit</TableHead><TableHead className="text-right">Total Kredit</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                <TableBody>
                  {glSummary.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-500 text-sm">Tidak ada aktivitas jurnal di periode ini.</TableCell></TableRow> : glSummary.map((a) => (
                    <TableRow key={a.account_code} className={`${a.is_parent ? "bg-amber-50/40" : "cursor-pointer hover:bg-slate-50"}`} onClick={() => { if (!a.is_parent) loadGlDetail(a.account_code); }} data-testid={`gl-row-${a.account_code}`}>
                      <TableCell className="font-mono-plex text-xs">{a.account_code}</TableCell>
                      <TableCell className={a.is_parent ? "font-semibold" : "font-medium"}><span className="inline-block" style={{ paddingLeft: `${(a.depth || 0) * 20}px` }}>{a.account_name}</span></TableCell>
                      <TableCell className="tabular-nums text-right">{formatIDR(a.debit)}</TableCell>
                      <TableCell className="tabular-nums text-right">{formatIDR(a.credit)}</TableCell>
                      <TableCell className={`tabular-nums text-right font-semibold ${a.balance >= 0 ? "text-slate-900" : "text-rose-700"}`}>{formatIDR(a.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Button variant="outline" size="sm" onClick={() => { setSelectedAcc(""); setGlDetail(null); loadGlSummary(); }} data-testid="back-to-summary"><ArrowLeft size={14} className="mr-1" />Kembali</Button>
                {glDetail && <div><span className="font-mono-plex text-xs text-slate-500 mr-2">{glDetail.account_code}</span><span className="font-medium">{glDetail.account_name}</span><span className="ml-2 text-xs text-slate-500">(Sisi Normal: {glDetail.normal_side})</span></div>}
              </div>
              {glDetail && (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                    <div className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs uppercase text-slate-500">Saldo Awal</div><div className="mt-1 font-serif-display text-xl font-semibold tabular-nums">{formatIDR(glDetail.opening_balance)}</div></div>
                    <div className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs uppercase text-slate-500">Total Debit</div><div className="mt-1 font-serif-display text-xl font-semibold tabular-nums text-emerald-700">{formatIDR(glDetail.total_debit)}</div></div>
                    <div className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs uppercase text-slate-500">Total Kredit</div><div className="mt-1 font-serif-display text-xl font-semibold tabular-nums text-rose-700">{formatIDR(glDetail.total_credit)}</div></div>
                    <div className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs uppercase text-slate-500">Saldo Akhir</div><div className="mt-1 font-serif-display text-xl font-semibold tabular-nums">{formatIDR(glDetail.closing_balance)}</div></div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-md" data-testid="gl-detail">
                    <Table>
                      <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Ref</TableHead><TableHead>Deskripsi</TableHead><TableHead>Sumber</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {glDetail.transactions.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">Belum ada transaksi.</TableCell></TableRow> : glDetail.transactions.map((t, i) => (
                          <TableRow key={i}>
                            <TableCell>{formatDate(t.date)}</TableCell>
                            <TableCell className="font-mono-plex text-xs">{t.reference || "—"}</TableCell>
                            <TableCell className="text-sm">{t.description}</TableCell>
                            <TableCell><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{t.source || "manual"}</span></TableCell>
                            <TableCell className="tabular-nums text-right">{t.debit > 0 ? formatIDR(t.debit) : "—"}</TableCell>
                            <TableCell className="tabular-nums text-right">{t.credit > 0 ? formatIDR(t.credit) : "—"}</TableCell>
                            <TableCell className="tabular-nums text-right font-semibold">{formatIDR(t.balance)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="spt" className="mt-4" data-testid="spt-tab">
          <div className="bg-white border border-slate-200 rounded-md p-4 mb-3 flex flex-wrap items-end gap-3">
            <div><Label>Masa Pajak (YYYY-MM)</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-02" data-testid="spt-period-input" /></div>
            <Button variant="outline" onClick={loadSpt} data-testid="spt-refresh-btn"><Receipt size={14} className="mr-1" />Refresh</Button>
            <Button variant="outline" onClick={async () => {
              try {
                const token = localStorage.getItem("nusa_token");
                const resp = await fetch(`${window.location.origin.replace(/\/$/, '')}/api/reports/spt-ppn/xml?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
                if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || "Gagal"); }
                const blob = await resp.blob(); const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `SPT_PPN_${period}.xml`; a.click();
                toast.success(`SPT Masa PPN ${period} diunduh`);
              } catch (e) { toast.error(e.message); }
            }} data-testid="spt-xml-btn" className="ml-auto bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100">Download SPT XML (DJP)</Button>
          </div>
          {spt && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div className="bg-white border border-slate-200 rounded-md p-4">
                  <div className="text-xs uppercase text-slate-500">PPN Keluaran (Penjualan)</div>
                  <div className="mt-1 font-serif-display text-2xl font-semibold tabular-nums text-emerald-700">{formatIDR(spt.outgoing.total_ppn)}</div>
                  <div className="text-xs text-slate-500 mt-1">{spt.outgoing.count} faktur · DPP {formatIDR(spt.outgoing.total_dpp)}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-md p-4">
                  <div className="text-xs uppercase text-slate-500">PPN Masukan (Pembelian)</div>
                  <div className="mt-1 font-serif-display text-2xl font-semibold tabular-nums text-sky-700">{formatIDR(spt.incoming.total_ppn)}</div>
                  <div className="text-xs text-slate-500 mt-1">{spt.incoming.count} faktur · DPP {formatIDR(spt.incoming.total_dpp)}</div>
                </div>
                <div className={`border rounded-md p-4 ${spt.net_ppn > 0 ? "bg-rose-50 border-rose-200" : spt.net_ppn < 0 ? "bg-sky-50 border-sky-200" : "bg-slate-50 border-slate-200"}`}>
                  <div className="text-xs uppercase text-slate-500">{spt.status}</div>
                  <div className={`mt-1 font-serif-display text-2xl font-semibold tabular-nums ${spt.net_ppn > 0 ? "text-rose-700" : spt.net_ppn < 0 ? "text-sky-700" : "text-slate-700"}`}>{formatIDR(Math.abs(spt.net_ppn))}</div>
                  <div className="text-xs text-slate-500 mt-1">Selisih PPN Keluaran − Masukan</div>
                </div>
              </div>
              <div className="grid lg:grid-cols-2 gap-3">
                <div className="bg-white border border-slate-200 rounded-md" data-testid="spt-outgoing">
                  <div className="px-4 py-2 border-b border-slate-200 font-medium text-sm">Faktur Pajak Keluaran</div>
                  <Table>
                    <TableHeader><TableRow><TableHead>No. e-Faktur</TableHead><TableHead>Tgl</TableHead><TableHead>Pembeli</TableHead><TableHead className="text-right">DPP</TableHead><TableHead className="text-right">PPN</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {spt.outgoing.items.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500 text-xs">Belum ada.</TableCell></TableRow> : spt.outgoing.items.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono-plex text-xs">{r.tax_invoice_number}{r.opt !== "Normal" && <span className="ml-1 text-[10px] px-1 rounded bg-amber-100 text-amber-700">{r.opt}</span>}</TableCell>
                          <TableCell className="text-xs">{r.date}</TableCell>
                          <TableCell className="text-xs">{r.buyer}</TableCell>
                          <TableCell className="tabular-nums text-right text-xs">{formatIDR(r.dpp)}</TableCell>
                          <TableCell className="tabular-nums text-right text-xs font-semibold">{formatIDR(r.ppn)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="bg-white border border-slate-200 rounded-md" data-testid="spt-incoming">
                  <div className="px-4 py-2 border-b border-slate-200 font-medium text-sm">Faktur Pajak Masukan</div>
                  <Table>
                    <TableHeader><TableRow><TableHead>No. Faktur Pemasok</TableHead><TableHead>Tgl</TableHead><TableHead>Pemasok</TableHead><TableHead className="text-right">DPP</TableHead><TableHead className="text-right">PPN</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {spt.incoming.items.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500 text-xs">Belum ada.</TableCell></TableRow> : spt.incoming.items.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono-plex text-xs">{r.supplier_tax_number}</TableCell>
                          <TableCell className="text-xs">{r.date}</TableCell>
                          <TableCell className="text-xs">{r.supplier}</TableCell>
                          <TableCell className="tabular-nums text-right text-xs">{formatIDR(r.dpp)}</TableCell>
                          <TableCell className="tabular-nums text-right text-xs font-semibold">{formatIDR(r.ppn)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="tb" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md" data-testid="tb-table">
            <Table>
              <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Akun</TableHead><TableHead>Tipe</TableHead><TableHead className="text-right">Total Debit</TableHead><TableHead className="text-right">Total Kredit</TableHead><TableHead className="text-right">Saldo Debit</TableHead><TableHead className="text-right">Saldo Kredit</TableHead></TableRow></TableHeader>
              <TableBody>
                {!tb ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">Memuat...</TableCell></TableRow> : tb.rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">Tidak ada aktivitas.</TableCell></TableRow> : tb.rows.map((r) => (
                  <TableRow key={r.code} className={r.is_parent ? "bg-amber-50/40" : ""} data-testid={`tb-row-${r.code}`}>
                    <TableCell className="font-mono-plex text-xs">{r.code}</TableCell>
                    <TableCell className={r.is_parent ? "font-semibold" : "font-medium"}><span className="inline-block" style={{ paddingLeft: `${(r.depth || 0) * 20}px` }}>{r.name}</span></TableCell>
                    <TableCell className="text-xs text-slate-500 capitalize">{r.type}</TableCell>
                    <TableCell className="tabular-nums text-right">{formatIDR(r.debit_total)}</TableCell>
                    <TableCell className="tabular-nums text-right">{formatIDR(r.credit_total)}</TableCell>
                    <TableCell className="tabular-nums text-right font-semibold">{r.debit_balance > 0 ? formatIDR(r.debit_balance) : "—"}</TableCell>
                    <TableCell className="tabular-nums text-right font-semibold">{r.credit_balance > 0 ? formatIDR(r.credit_balance) : "—"}</TableCell>
                  </TableRow>
                ))}
                {tb && tb.rows.length > 0 && (
                  <TableRow className="bg-slate-50 border-t-2 border-slate-900" data-testid="tb-totals">
                    <TableCell colSpan={3} className="font-bold">TOTAL</TableCell>
                    <TableCell className="tabular-nums text-right font-bold">{formatIDR(tb.total_debit)}</TableCell>
                    <TableCell className="tabular-nums text-right font-bold">{formatIDR(tb.total_credit)}</TableCell>
                    <TableCell colSpan={2} className="text-right">
                      {tb.balanced ? <span className="text-emerald-700 text-xs font-semibold inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" />Neraca Saldo BALANCED</span> : <span className="text-rose-700 text-xs font-semibold inline-flex items-center gap-1"><XCircle size={14} weight="fill" />TIDAK BALANCE (selisih {formatIDR(Math.abs(tb.total_debit - tb.total_credit))})</span>}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
